import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Search } from "lucide-react";
import { todayIso } from "@shared/originationByPath";

interface Candidate {
  id: string; name: string; category: string; industry: string | null; headquarters: string | null;
  currentSourceType: string | null; currentContactName: string | null;
}

/**
 * Attribute a prospect (or client) that already exists to this COI.
 * "New prospect from this COI" covers the day the name arrives; this covers
 * the ones that were already in the CRM before anyone wrote down who sent
 * them. Writes the same origination row through the same endpoint.
 */
export function AttributeExistingDialog({ open, onOpenChange, coi }: {
  open: boolean; onOpenChange: (o: boolean) => void; coi: { id: string; name: string };
}) {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<Candidate | null>(null);
  const [occurredOn, setOccurredOn] = useState(todayIso());
  const [note, setNote] = useState("");

  const { data: candidates = [], isFetching } = useQuery<Candidate[]>({
    queryKey: ["origination-candidates", q],
    queryFn: async () => {
      const r = await fetch(`/api/origination/candidates?q=${encodeURIComponent(q)}`, { credentials: "include" });
      return r.ok ? r.json() : [];
    },
    enabled: open,
  });

  const attribute = useMutation({
    mutationFn: async () => {
      if (!picked) throw new Error("Pick a company");
      const r = await fetch(`/api/companies/${picked.id}/origination`, {
        method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sourceType: "coi_referral", contactId: coi.id, occurredOn: occurredOn || null, note: note.trim() || null }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Failed");
    },
    onSuccess: () => {
      for (const key of [["coi", coi.id], ["cois"], ["coi-bd"], ["/api/prospects"], ["origination"], ["suggestions"]]) qc.invalidateQueries({ queryKey: key });
      toast.success(`${picked!.name} attributed to ${coi.name}`);
      setPicked(null); setNote(""); setQ("");
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!attribute.isPending) onOpenChange(o); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Attribute an existing prospect to {coi.name}</DialogTitle></DialogHeader>
        {!picked ? (
          <div className="space-y-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input autoFocus className="pl-8" placeholder="Search prospects and clients" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div className="max-h-72 overflow-y-auto divide-y rounded-md border">
              {candidates.length === 0 && (
                <p className="px-3 py-4 text-sm text-muted-foreground italic">{isFetching ? "Searching…" : "No matches."}</p>
              )}
              {candidates.map((c) => (
                <button key={c.id} type="button" className="w-full text-left px-3 py-2 hover:bg-muted/50" onClick={() => setPicked(c)}>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{c.name}</span>
                    <Badge variant="outline" className="text-[10px]">{c.category === "Client" ? "Client" : "Prospect"}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {[c.industry, c.headquarters].filter(Boolean).join(" · ") || "—"}
                    {c.currentSourceType
                      ? <span className="ml-2 text-amber-700 dark:text-amber-400">· already attributed{c.currentContactName ? ` to ${c.currentContactName}` : ""}, will be replaced</span>
                      : <span className="ml-2">· no source recorded</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md border px-3 py-2 text-sm flex items-center justify-between">
              <span><span className="font-medium">{picked.name}</span> <span className="text-muted-foreground">· {picked.category === "Client" ? "Client" : "Prospect"}</span></span>
              <button className="text-xs text-primary-text hover:underline" onClick={() => setPicked(null)}>Change</button>
            </div>
            {picked.currentSourceType && (
              <p className="text-xs text-amber-700 dark:text-amber-400">This company is already attributed{picked.currentContactName ? ` to ${picked.currentContactName}` : ""}. Saving replaces that with {coi.name}.</p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">When they introduced it</Label><Input type="date" value={occurredOn} onChange={(e) => setOccurredOn(e.target.value)} /></div>
              <div className="space-y-1"><Label className="text-xs">Note</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Met the owner at the Chamber lunch" /></div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={attribute.isPending}>Cancel</Button>
              <Button onClick={() => attribute.mutate()} disabled={attribute.isPending}>Attribute to {coi.name.split(" ")[0]}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
